import { NETCODE_V4_TICK_RATE, normalizeRadians } from '@star-snake/shared'
import type { ChainPointV4, DecodedRemoteChainV4 } from '../protocol/BinaryDecoderV4'

const MAX_SNAPSHOTS = 8
const ONE_TICK_MS = 1000 / NETCODE_V4_TICK_RATE
const MAX_EXTRAPOLATION_TICKS = 1
const TIER_BLEND_MS = 100

export interface RemoteChainSnapshotV4 extends DecodedRemoteChainV4 {
  serverTick: number
}

export interface RenderedRemoteChainV4 {
  spawnToken: number
  fidelity: number
  flags: number
  length: number
  points: ChainPointV4[]
  bearing: number
}

export class RemoteChainBuffer {
  private spawnToken = 0
  private snapshots: RemoteChainSnapshotV4[] = []
  private readonly renderedScratch: ChainPointV4[] = []
  private readonly resampleScratchA: ChainPointV4[] = []
  private readonly resampleScratchB: ChainPointV4[] = []
  private readonly cumulativeScratchA: number[] = []
  private readonly cumulativeScratchB: number[] = []

  pushSnapshot(snapshot: RemoteChainSnapshotV4): void {
    if (this.spawnToken !== 0 && snapshot.spawnToken !== this.spawnToken) {
      this.snapshots = []
    }

    this.spawnToken = snapshot.spawnToken
    const storedSnapshot = {
      ...snapshot,
      points: snapshot.points.map((point) => ({ x: point.x, z: point.z })),
    }
    const lastSnapshot = this.snapshots[this.snapshots.length - 1]
    if (!lastSnapshot || storedSnapshot.serverTick >= lastSnapshot.serverTick) {
      this.snapshots.push(storedSnapshot)
    } else {
      let insertAt = this.snapshots.length
      while (insertAt > 0 && this.snapshots[insertAt - 1].serverTick > storedSnapshot.serverTick) {
        insertAt--
      }
      this.snapshots.splice(insertAt, 0, storedSnapshot)
    }
    if (this.snapshots.length > MAX_SNAPSHOTS) {
      this.snapshots.splice(0, this.snapshots.length - MAX_SNAPSHOTS)
    }
  }

  clear(): void {
    this.spawnToken = 0
    this.snapshots = []
    this.renderedScratch.length = 0
    this.resampleScratchA.length = 0
    this.resampleScratchB.length = 0
    this.cumulativeScratchA.length = 0
    this.cumulativeScratchB.length = 0
  }

  getRendered(renderTick: number): RenderedRemoteChainV4 | null {
    if (this.snapshots.length === 0) return null

    const oldest = this.snapshots[0]
    const newest = this.snapshots[this.snapshots.length - 1]

    if (renderTick <= oldest.serverTick) {
      return this.toRendered(oldest, oldest.points)
    }

    let before: RemoteChainSnapshotV4 | null = null
    let after: RemoteChainSnapshotV4 | null = null
    for (let i = 0; i < this.snapshots.length - 1; i++) {
      const left = this.snapshots[i]
      const right = this.snapshots[i + 1]
      if (renderTick >= left.serverTick && renderTick <= right.serverTick) {
        before = left
        after = right
        break
      }
    }

    if (before && after) {
      if (before.fidelity === after.fidelity) {
        const alpha = (renderTick - before.serverTick) / Math.max(1, after.serverTick - before.serverTick)
        const points = interpolateChains(
          before.points,
          after.points,
          alpha,
          this.resampleScratchA,
          this.resampleScratchB,
          this.renderedScratch,
          this.cumulativeScratchA,
          this.cumulativeScratchB,
        )
        return this.toRendered(after, points)
      }

      if (renderTick < after.serverTick) {
        return this.toRendered(before, before.points)
      }

      const elapsedMs = Math.min(
        TIER_BLEND_MS,
        Math.max(0, (renderTick - after.serverTick + 1) * ONE_TICK_MS),
      )
      const alpha = elapsedMs / TIER_BLEND_MS
      const points = interpolateChains(
        before.points,
        after.points,
        alpha,
        this.resampleScratchA,
        this.resampleScratchB,
        this.renderedScratch,
        this.cumulativeScratchA,
        this.cumulativeScratchB,
      )
      return this.toRendered(after, points)
    }

    const tickDelta = renderTick - newest.serverTick
    if (tickDelta > 0 && tickDelta <= MAX_EXTRAPOLATION_TICKS && this.snapshots.length >= 2) {
      const previous = this.snapshots[this.snapshots.length - 2]
      const extrapolated = extrapolateHeadOnly(previous, newest, tickDelta, this.renderedScratch)
      return this.toRendered(newest, extrapolated)
    }

    return this.toRendered(newest, newest.points)
  }

  private toRendered(snapshot: RemoteChainSnapshotV4, points: ChainPointV4[]): RenderedRemoteChainV4 {
    const bearing = points.length >= 2
      ? normalizeRadians(Math.atan2(points[0].z - points[1].z, points[0].x - points[1].x))
      : 0

    return {
      spawnToken: snapshot.spawnToken,
      fidelity: snapshot.fidelity,
      flags: snapshot.flags,
      length: snapshot.length,
      points,
      bearing,
    }
  }
}

function interpolateChains(
  a: readonly ChainPointV4[],
  b: readonly ChainPointV4[],
  alpha: number,
  aResampled: ChainPointV4[],
  bResampled: ChainPointV4[],
  result: ChainPointV4[],
  aCumulative: number[],
  bCumulative: number[],
): ChainPointV4[] {
  const sampleCount = Math.max(2, Math.max(a.length, b.length))
  resampleChain(a, sampleCount, aResampled, aCumulative)
  resampleChain(b, sampleCount, bResampled, bCumulative)
  const clampedAlpha = Math.max(0, Math.min(1, alpha))

  ensurePointArray(result, sampleCount)
  for (let i = 0; i < sampleCount; i++) {
    const point = result[i]
    const left = aResampled[i]
    const right = bResampled[i]
    point.x = left.x + (right.x - left.x) * clampedAlpha
    point.z = left.z + (right.z - left.z) * clampedAlpha
  }
  result.length = sampleCount
  return result
}

function extrapolateHeadOnly(
  previous: RemoteChainSnapshotV4,
  newest: RemoteChainSnapshotV4,
  tickDelta: number,
  target: ChainPointV4[],
): ChainPointV4[] {
  copyChainPoints(newest.points, target)
  if (previous.points.length === 0 || newest.points.length === 0) return target

  const tickSpan = Math.max(1, newest.serverTick - previous.serverTick)
  const velocityX = (newest.points[0].x - previous.points[0].x) / tickSpan
  const velocityZ = (newest.points[0].z - previous.points[0].z) / tickSpan
  target[0].x += velocityX * tickDelta
  target[0].z += velocityZ * tickDelta
  return target
}

function resampleChain(
  points: readonly ChainPointV4[],
  sampleCount: number,
  target: ChainPointV4[],
  cumulative: number[],
): ChainPointV4[] {
  if (points.length === 0) {
    target.length = 0
    cumulative.length = 0
    return target
  }
  if (points.length === 1 || sampleCount <= 1) {
    ensurePointArray(target, 1)
    target[0].x = points[0].x
    target[0].z = points[0].z
    target.length = 1
    cumulative.length = 1
    cumulative[0] = 0
    return target
  }

  cumulative.length = points.length
  cumulative[0] = 0
  for (let i = 1; i < points.length; i++) {
    cumulative[i] = cumulative[i - 1] + Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z)
  }

  const totalLength = cumulative[cumulative.length - 1]
  if (totalLength <= 0.0001) {
    ensurePointArray(target, sampleCount)
    for (let i = 0; i < sampleCount; i++) {
      target[i].x = points[0].x
      target[i].z = points[0].z
    }
    target.length = sampleCount
    return target
  }

  ensurePointArray(target, sampleCount)
  for (let i = 0; i < sampleCount; i++) {
    const targetDistance = (totalLength * i) / (sampleCount - 1)
    let segmentIndex = 1
    while (segmentIndex < cumulative.length && cumulative[segmentIndex] < targetDistance) segmentIndex++
    const endIndex = Math.min(segmentIndex, points.length - 1)
    const startIndex = Math.max(0, endIndex - 1)
    const startDistance = cumulative[startIndex]
    const endDistance = cumulative[endIndex]
    const span = Math.max(0.0001, endDistance - startDistance)
    const t = (targetDistance - startDistance) / span
    target[i].x = points[startIndex].x + (points[endIndex].x - points[startIndex].x) * t
    target[i].z = points[startIndex].z + (points[endIndex].z - points[startIndex].z) * t
  }

  target.length = sampleCount
  return target
}

function ensurePointArray(target: ChainPointV4[], count: number): void {
  for (let i = target.length; i < count; i++) {
    target.push({ x: 0, z: 0 })
  }
  target.length = count
}

function copyChainPoints(source: readonly ChainPointV4[], target: ChainPointV4[]): ChainPointV4[] {
  ensurePointArray(target, source.length)
  for (let i = 0; i < source.length; i++) {
    target[i].x = source[i].x
    target[i].z = source[i].z
  }
  return target
}
